import { FC, ReactNode, useCallback, useEffect, useMemo, useState } from 'react'
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Stack,
  TablePagination,
  Tooltip,
  Typography,
} from '@mui/material'
import DownloadIcon from '@mui/icons-material/Download'
import RefreshIcon from '@mui/icons-material/Refresh'
import dayjs from 'dayjs'
import { useSnackbar } from 'notistack'
import { PageHeader } from '@/components/common/PageHeader'
import { KpiCard } from '@/components/common/KpiCard'
import { TabelaAcao } from '@/components/common/TabelaAcao'
import { EstadoDeLista } from '@/components/common/EstadoDeLista'
import { auditoriaConsultorService } from '@/services/auditoriaConsultorService'
import { EventoAuditoria } from '@/types/auditoria'
import { PageResponse } from '@/types/comum'
import { tokens } from '@/theme'

const LOCAL_DATE_TIME = 'YYYY-MM-DDTHH:mm:ss'
const NAO_REGISTRADO = '— (não registrado pelo backend)'

/** Rótulo curto da ação (`UPDATE` → `Alteração`) — o valor bruto fica no modal. */
const ROTULO_ACAO: Record<string, string> = {
  CREATE: 'Criação',
  CRIACAO: 'Criação',
  UPDATE: 'Alteração',
  ATUALIZACAO: 'Alteração',
  DELETE: 'Exclusão',
  EXCLUSAO: 'Exclusão',
  LOGIN: 'Acesso',
  READ: 'Consulta',
  CONSULTA: 'Consulta',
}

const rotuloAcao = (acao: string): string => ROTULO_ACAO[acao?.toUpperCase()] ?? acao

/**
 * Entidade no formato do Figma (`OP-2841`): o recurso gravado e o id encurtado. O gravador
 * envia o id inteiro (UUID) — o completo aparece no tooltip e no modal.
 */
const entidadeDe = (ev: EventoAuditoria): string => {
  if (!ev.recursoId) return ev.recurso
  const id = ev.recursoId.length > 8 ? ev.recursoId.slice(0, 8) : ev.recursoId
  return `${ev.recurso} · ${id}`
}

const textoDe = (v: unknown): string | null =>
  typeof v === 'string' && v.trim() ? v : typeof v === 'number' ? String(v) : null

/** `resultado` e `correlationId` só existem se o gravador os puser em `detalhes` — hoje não põe. */
const resultadoDe = (ev: EventoAuditoria): string | null => textoDe(ev.detalhes?.resultado)
const correlacaoDe = (ev: EventoAuditoria): string | null =>
  textoDe(ev.detalhes?.correlationId) ?? textoDe(ev.detalhes?.correlacaoId)

const Campo: FC<{ rotulo: string; children: ReactNode }> = ({ rotulo, children }) => (
  <Box>
    <Typography variant="caption" sx={{ textTransform: 'uppercase', letterSpacing: '0.08em', color: 'text.secondary' }}>
      {rotulo}
    </Typography>
    <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
      {children}
    </Typography>
  </Box>
)

const Json: FC<{ valor?: Record<string, unknown> | null }> = ({ valor }) =>
  valor && Object.keys(valor).length ? (
    <Box
      component="pre"
      sx={{
        m: 0,
        p: 1.5,
        fontSize: 12,
        borderRadius: 1,
        overflow: 'auto',
        maxHeight: 240,
        backgroundColor: tokens.surfaceCard,
        border: `1px solid ${tokens.divider}`,
      }}
    >
      {JSON.stringify(valor, null, 2)}
    </Box>
  ) : (
    <Typography variant="body2" color="text.secondary">
      —
    </Typography>
  )

/**
 * Auditoria do consultor (Figma 27:144, Onda 8 / A17): a trilha das ações do PRÓPRIO
 * consultor. Não há filtro de ator nem de tenant aqui — nem no serviço: o backend força
 * `usuario_id = sub` e o tenant do claim no `WHERE`, e um evento de outro ator responde 404.
 *
 * KPIs: "Eventos" e "Últimos 7 dias" saem de `GET /v1/auditoria` (total da própria
 * consulta, com e sem `inicio`). "Reservas tocadas" e "Falhas de acesso" ficam em "—":
 * não há endpoint de resumo e o gravador não registra resultado.
 */
const AuditoriaConsultorPage: FC = () => {
  const { enqueueSnackbar } = useSnackbar()

  const [pagina, setPagina] = useState<PageResponse<EventoAuditoria> | null>(null)
  const [ultimos7, setUltimos7] = useState<number | null>(null)
  const [page, setPage] = useState(0)
  const [size, setSize] = useState(20)
  const [carregando, setCarregando] = useState(true)
  const [erro, setErro] = useState<string | null>(null)
  const [estadoErro, setEstadoErro] = useState<'erro' | 'semPermissao'>('erro')
  const [exportando, setExportando] = useState(false)

  const [detalhe, setDetalhe] = useState<EventoAuditoria | null>(null)
  const [carregandoDetalhe, setCarregandoDetalhe] = useState(false)

  const carregar = useCallback(async () => {
    setCarregando(true)
    setErro(null)
    try {
      const inicio7 = dayjs().subtract(7, 'day').format(LOCAL_DATE_TIME)
      const [lista, semana] = await Promise.all([
        auditoriaConsultorService.listar({ page, size }),
        auditoriaConsultorService.listar({ page: 0, size: 1, inicio: inicio7 }),
      ])
      setPagina(lista)
      setUltimos7(semana.totalElements)
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Falha ao carregar a auditoria')
      setEstadoErro((e as { status?: number })?.status === 403 ? 'semPermissao' : 'erro')
    } finally {
      setCarregando(false)
    }
  }, [page, size])

  useEffect(() => {
    void carregar()
  }, [carregar])

  const abrir = async (ev: EventoAuditoria) => {
    setDetalhe(ev)
    setCarregandoDetalhe(true)
    try {
      // A listagem já traz o evento inteiro; o GET por id confirma o escopo (404 fora dele).
      setDetalhe(await auditoriaConsultorService.obter(ev.id))
    } catch (e) {
      const status = (e as { status?: number })?.status
      enqueueSnackbar(
        status === 404 ? 'Evento não encontrado na sua trilha' : e instanceof Error ? e.message : 'Falha ao abrir o evento',
        { variant: status === 404 ? 'warning' : 'error' }
      )
      setDetalhe(null)
    } finally {
      setCarregandoDetalhe(false)
    }
  }

  const exportar = async () => {
    setExportando(true)
    try {
      const blob = await auditoriaConsultorService.exportarCsv()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `minha-auditoria-${dayjs().format('YYYY-MM-DD')}.csv`
      a.click()
      URL.revokeObjectURL(url)
    } catch (e) {
      enqueueSnackbar(e instanceof Error ? e.message : 'Falha ao exportar CSV', { variant: 'error' })
    } finally {
      setExportando(false)
    }
  }

  const linhas = useMemo(() => pagina?.content ?? [], [pagina])
  const total = pagina?.totalElements ?? 0

  return (
    <Box>
      <PageHeader
        title="Auditoria"
        subtitle="A trilha das suas ações no portal — só o que você fez, com data, hora e origem"
        eyebrow="Sua conta"
        acao={
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" startIcon={<RefreshIcon />} onClick={() => void carregar()} disabled={carregando}>
              Atualizar
            </Button>
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={() => void exportar()}
              disabled={exportando || total === 0}
            >
              Exportar CSV
            </Button>
          </Stack>
        }
      />

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          {/* fonte: GET /v1/auditoria (totalElements da própria consulta, escopo do backend) */}
          <KpiCard label="Eventos" valor={pagina ? total : '—'} legenda="Registrados na sua trilha" variante="escura" carregando={carregando} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          {/* fonte: GET /v1/auditoria?inicio=hoje-7d (totalElements) */}
          <KpiCard label="Últimos 7 dias" valor={ultimos7 ?? '—'} legenda="Desde a semana passada" carregando={carregando} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          {/* sem endpoint de resumo: não há como contar reservas distintas sem carregar tudo */}
          <KpiCard label="Reservas tocadas" valor="—" legenda="Sem resumo no backend" carregando={carregando} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          {/* o gravador não registra resultado (sucesso/negado) — ver relatório A17 */}
          <KpiCard label="Falhas de acesso" valor="—" legenda="Não registrado pelo backend" carregando={carregando} />
        </Grid>
      </Grid>

      {erro ? (
        <EstadoDeLista
          estado={estadoErro}
          {...(estadoErro === 'erro' && {
            mensagem: 'Não foi possível carregar a sua auditoria',
            detalhe: erro,
            acao: { label: 'Tentar novamente', onClick: () => void carregar() },
          })}
        />
      ) : carregando && !pagina ? (
        <EstadoDeLista estado="carregando" />
      ) : linhas.length === 0 ? (
        <EstadoDeLista estado="vazio" mensagem="Nenhuma ação sua registrada ainda" detalhe="Quando você criar ou alterar algo no portal, o evento aparece aqui." />
      ) : (
        <>
          <TabelaAcao<EventoAuditoria>
            aria-label="Minha auditoria"
            linhas={linhas}
            chaveDaLinha={(ev) => ev.id}
            onAbrir={(ev) => void abrir(ev)}
            colunas={[
              {
                titulo: 'Evento',
                render: (ev) => (
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {rotuloAcao(ev.acao)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {ev.descricao ?? ev.modulo}
                    </Typography>
                  </Box>
                ),
              },
              {
                titulo: 'Quando',
                largura: 170,
                render: (ev) => (
                  <Box>
                    <Typography variant="body2">{dayjs(ev.ocorridoEm).format('DD/MM/YYYY')}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {dayjs(ev.ocorridoEm).format('HH:mm:ss')}
                    </Typography>
                  </Box>
                ),
              },
              {
                titulo: 'Entidade',
                render: (ev) => (
                  <Tooltip title={ev.recursoId ?? ''}>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                      {entidadeDe(ev)}
                    </Typography>
                  </Tooltip>
                ),
              },
              {
                titulo: 'Resultado',
                largura: 140,
                render: (ev) => {
                  const r = resultadoDe(ev)
                  return r ? (
                    <Chip size="small" label={r} color={/neg|falh|erro/i.test(r) ? 'error' : 'success'} />
                  ) : (
                    <Tooltip title="O gravador de eventos ainda não registra o resultado">
                      <Typography variant="body2" color="text.secondary">
                        —
                      </Typography>
                    </Tooltip>
                  )
                },
              },
            ]}
          />
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={size}
            onRowsPerPageChange={(e) => {
              setSize(parseInt(e.target.value, 10))
              setPage(0)
            }}
            rowsPerPageOptions={[10, 20, 50]}
            labelRowsPerPage="Por página"
          />
        </>
      )}

      <Dialog open={!!detalhe} onClose={() => setDetalhe(null)} maxWidth="md" fullWidth aria-labelledby="detalhe-evento-titulo">
        {detalhe && (
          <>
            <DialogTitle id="detalhe-evento-titulo">
              {rotuloAcao(detalhe.acao)} · {entidadeDe(detalhe)}
              <Typography variant="body2" color="text.secondary" component="div">
                {dayjs(detalhe.ocorridoEm).format('DD/MM/YYYY HH:mm:ss')}
                {carregandoDetalhe ? ' · confirmando…' : ''}
              </Typography>
            </DialogTitle>
            <DialogContent dividers>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Campo rotulo="Requisição">
                    {correlacaoDe(detalhe) ? `REQ-${correlacaoDe(detalhe)}` : NAO_REGISTRADO}
                  </Campo>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Campo rotulo="Resultado">{resultadoDe(detalhe) ?? NAO_REGISTRADO}</Campo>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Campo rotulo="Módulo · Recurso">
                    {detalhe.modulo} · {detalhe.recurso}
                  </Campo>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Campo rotulo="Id do recurso">{detalhe.recursoId ?? '—'}</Campo>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Campo rotulo="Seu IP">{detalhe.ip ?? '—'}</Campo>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Campo rotulo="Seu dispositivo">{detalhe.userAgent ?? '—'}</Campo>
                </Grid>
                <Grid item xs={12}>
                  <Campo rotulo="Ator">{detalhe.usuarioEmail ?? detalhe.usuarioId ?? '—'}</Campo>
                </Grid>
                {detalhe.descricao && (
                  <Grid item xs={12}>
                    <Campo rotulo="Descrição">{detalhe.descricao}</Campo>
                  </Grid>
                )}
              </Grid>
              <Divider sx={{ my: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Antes
                  </Typography>
                  <Json valor={detalhe.payloadAntes} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Depois
                  </Typography>
                  <Json valor={detalhe.payloadDepois} />
                </Grid>
                {detalhe.detalhes && Object.keys(detalhe.detalhes).length > 0 && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Detalhes
                    </Typography>
                    <Json valor={detalhe.detalhes} />
                  </Grid>
                )}
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDetalhe(null)}>Fechar</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  )
}

export default AuditoriaConsultorPage
